/*
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2026-06-30 09:47:49 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-07-27 15:01:17 +0800
 * @FilePath     : /ai-agent-lumina/src/utils/server/agent-request.js
 * @Description  : agent request
 */
import axios from 'axios';
import { contentEnum } from './http-enum'
import { serialize } from '@/utils';
import { useUserStore } from '@/stores/modules/user.js'
import { useConfigStore } from '@/stores/modules/config.js'
import { showToast } from 'vant';
import { encrypt } from '@/utils/sm2';

const request = axios.create({
  baseURL: import.meta.env.VITE_APP_AGENTBASEPATH,
  withCredentials: false, // 跨域请求是否发送 cookies
  timeout: 1000 * 60,
})

request.interceptors.request.use(
  config => {
    return config;
  },
  error => {
    return Promise.reject(error);
  }
)

request.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    return Promise.reject(error);
  }
)


/**
 * agent请求
 * @author: BYM<yongming.bai@outlook.com>
 */
export const agentApi = (url, method = 'post', data = {}, options = {}) => {
  const configStore = useConfigStore()
  const methodUpper = method.toUpperCase()

  const agentConfig = {
    url: url,
    method: methodUpper,
    headers: {
      Authorization: `Bearer ${configStore.appSecret}`,
      appId: configStore.appId,
    },
    ...options
  }
  if (method.toLocaleUpperCase() === 'GET') {
    agentConfig.params = data
  } else {
    agentConfig.data = data
  }
  return request(agentConfig)
}


/**
 * agent流式请求（SSE）
 * 使用 XHR onprogress 实现，兼容所有主流浏览器及国产 WebView（IE10+）
 * @param {string} url - 接口路径
 * @param {object} data - 请求体
 * @param {object} callbacks - 回调函数集合
 * @param {function} callbacks.onMessage - 收到消息回调，参数为(parsed, event)
 * @param {function} callbacks.onComplete - 流结束回调
 * @param {function} callbacks.onError - 错误回调
 * @returns {object} requestTask - 请求任务对象，可调用 abort() 中断
 */
export const agentStreamApi = (url, data = {}, callbacks = {}) => {
  const configStore = useConfigStore()
  const userStore = useUserStore()
  const { onMessage, onComplete, onError } = callbacks
  const enableNotice = userStore.agentSetting?.roleAndStyle?.enableNotice

  const fullUrl = `${import.meta.env.VITE_APP_AGENTBASEPATH}${url}`

  let isThink = true
  if (enableNotice !== undefined && enableNotice !== null) {
    isThink = enableNotice
  }

  // 使用展开运算符避免污染原始 data.inputs
  const rawTimestamp = Date.now()
  const timestampPlaintext = String(rawTimestamp)
  // 防御：校验加密前的明文，避免将空串送入加密导致后端解密得空值
  const encryptedTimestamp = encrypt(rawTimestamp)
  const inputs = {
    ...(data.inputs || {}),
    timestamp: encryptedTimestamp,
    send_date: timestampPlaintext,
    isThink,
    tenantId: userStore.exhibitionId,
    lang: userStore.lang || 'cn',
  }
  const settings = {}
  if (userStore?.account) inputs.user_id = userStore.account
  // 小助手配置
  if (userStore?.agentSetting?.roleAndStyle || userStore?.agentSetting?.specialRequirement) {
    if (userStore.agentSetting?.roleAndStyle?.role) settings.role = userStore.agentSetting.roleAndStyle.role
    if (userStore.agentSetting?.roleAndStyle?.style) settings.style = userStore.agentSetting.roleAndStyle.style
    if (userStore.agentSetting?.specialRequirement) settings.special = userStore.agentSetting.specialRequirement
  }
  // E展通配置同步
  if (configStore.eztConfig.e_exhibitionId) inputs.e_exhibitionId = configStore.eztConfig.e_exhibitionId
  if (configStore.eztConfig.e_tenantId) inputs.e_tenantId = configStore.eztConfig.e_tenantId

  const params = {
    ...data,
    inputs: {
      ...inputs,
      ...settings
    },
    response_mode: 'streaming',
  }

  const xhr = new XMLHttpRequest()
  let lastIndex = 0
  let buffer = ''
  let completed = false

  xhr.open('POST', fullUrl)
  xhr.setRequestHeader('Content-Type', 'application/json')
  xhr.setRequestHeader('Accept', 'text/event-stream')
  xhr.setRequestHeader('Authorization', `Bearer ${configStore.appSecret}`)
  xhr.setRequestHeader('appId', configStore.appId)

  xhr.onprogress = () => {
    // 增量读取 responseText 中新到达的数据
    const newText = xhr.responseText.substring(lastIndex)
    lastIndex = xhr.responseText.length
    if (!newText) return

    buffer += newText
    // 按双换行分割完整的 SSE 事件块
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].trim()) parseSSEEvent(parts[i], onMessage)
    }
  }

  xhr.onload = () => {
    if (completed) return
    completed = true
    // 处理缓冲区中可能残留的最后一段数据
    if (buffer.trim()) parseSSEBuffer(buffer, onMessage)
    onComplete && onComplete()
  }

  xhr.onerror = () => {
    if (completed) return
    completed = true
    onError && onError(new Error('Network request failed'))
  }

  xhr.ontimeout = () => {
    if (completed) return
    completed = true
    onError && onError(new Error('Request timeout'))
  }

  xhr.onabort = () => {
    if (completed) return
    completed = true
    onComplete && onComplete()
  }

  xhr.send(JSON.stringify(params))

  return {
    abort: () => xhr.abort(),
  }
}

/**
 * 解析SSE缓冲区中的剩余数据
 */
function parseSSEBuffer(buffer, onMessage) {
  const parts = buffer.split('\n\n')
  for (const part of parts) {
    if (!part.trim()) continue
    parseSSEEvent(part, onMessage)
  }
}

/**
 * 解析单个SSE事件
 */
function parseSSEEvent(eventStr, onMessage) {
  const lines = eventStr.split('\n')
  const dataLines = []
  let event = 'message'

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('data: ')) {
      dataLines.push(line.slice(6))
    } else if (line.startsWith('event: ')) {
      event = line.slice(7).trim()
    }
  }

  if (!dataLines.length) return

  // SSE 规范：多条 data: 行应以 \n 连接
  const data = dataLines.join('\n')

  try {
    const parsed = JSON.parse(data)
    onMessage && onMessage(parsed, event)
  } catch (e) {
    onMessage && onMessage(data, event)
  }
}

/**
 * agent文件上传（multipart/form-data）
 * 使用 FormData + axios 上传文件到 agent 接口
 * @param {string} url - 接口路径（不含 baseURL，如 '/app/audio-to-text'）
 * @param {File|Blob} file - 文件对象（File / Blob）
 * @param {object} formData - 附加的表单数据（如 { user: 'xxx' }）
 * @param {object} options - 额外配置（如自定义 header、name 字段名）
 * @returns {Promise<object>} 解析后的响应数据
 * @author: BYM<yongming.bai@outlook.com>
 */
export const agentUploadFile = (url, formData, options = {}) => {
  const configStore = useConfigStore()

  return request({
    url,
    baseURL: import.meta.env.VITE_APP_BASE_API,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${configStore.appSecret}`,
      'appId': configStore.appId,
      ...(options.headers || {}),
    },
  })
}